import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useStore, useDispatch } from "react-redux";
import { hinweiseActions } from "../actions";

function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(
        container.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
    ).filter(el => !el.hasAttribute('disabled'));
}

const HinweiseDisplay = (props) => {
    const { hinweiseStorage, configStorage } = useStore().getState();
    const dispatch = useDispatch();
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);    
    const isOpen = uncontrolledOpen;

    const [hinweise, setHinweise] = useState([]);
    const modalRef = useRef(null);
    const backdropRef = useRef(null);
    const lastActiveElementRef = useRef(null);

    const sendGelesenStatus = useCallback(async () => {
    
        let requestSendHinweise = configStorage.getRequestData("hinweise", "post", { hinweise: hinweiseStorage.getHinweise() });
        const result = await dispatch(hinweiseActions.storeHinweise(requestSendHinweise))
            .catch(err => {
                throw err;            
        });

        return result;
    }, [hinweiseStorage, configStorage, dispatch]);

    const requestClose = useCallback(() => {
        sendGelesenStatus();        
        setUncontrolledOpen(false);        
    }, [sendGelesenStatus]);
    
    const toggleGelesen = useCallback((id, value) => {
        setHinweise(prev => {
            const next = prev.map(h => h.id === id ? { ...h, gelesen: !!value } : h);   
            hinweiseStorage.setGelesen(id);                     
            return next;
        });
    }, [hinweiseStorage]);

    useMemo(() => {
        const h = hinweiseStorage.getUnreadHinweise();            
        setHinweise(h || []);
        if ((h?.length > 0)) {
            setUncontrolledOpen(true);
        }
    }, [hinweiseStorage]);

    useEffect(() => {
        if (!isOpen) return;
        lastActiveElementRef.current = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('modal-open');

        const tryFocus = () => {
            const focusables = getFocusableElements(modalRef.current);
            if (focusables.length) focusables[0].focus();
            else modalRef.current?.focus?.();
        };
        tryFocus();

        const onKey = (e) => {
            if (e.key === 'Escape') {
                requestClose();
            } else if (e.key === 'Tab') {
                const focusables = getFocusableElements(modalRef.current);
                if (focusables.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
            document.body.classList.remove('modal-open');
            try {
                lastActiveElementRef.current?.focus?.();
            } catch (err) { /* ignore */ }
        };
    }, [isOpen, requestClose]);

    if (!isOpen || hinweise.length === 0) return null;

    const onBackdropClick = (e) => {
        if (e.target === backdropRef.current) {
            requestClose();
        }
    };

    const modal = (
        <>
            {/* Backdrop */}
            <div
                ref={backdropRef}
                className="modal-backdrop fade show"
                onClick={onBackdropClick}
                role="presentation"
            />

            {/* Visible modal */}
            <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="hinweise-title"
            >
                <div className={`modal-dialog modal-lg`} role="document">
                    <div className="modal-content" ref={modalRef}>
                        <div className="modal-header">
                            <h2 id="hinweise-title" className="modal-title">Hinweise</h2>
                            <button
                                type="button"
                                className="btn btn-default ser-close-modal"
                                aria-label="Close Hinweise"
                                onClick={requestClose}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-body">
                            {hinweise.map(h => {
                                const isRead = (h.gelesen === true || h.gelesen === 'true');
                                return (
                                    <section key={h.id} className="mb-3">
                                        <div className="form-check d-flex align-items-start">
                                            <input
                                                className="mt-1 me-2"
                                                type="checkbox"
                                                id={`hinweis-${h.id}`}
                                                checked={!!isRead}
                                                disabled={!!isRead}
                                                onChange={(e) => toggleGelesen(h.id, e.target.checked)}
                                                aria-label={`Markiere Hinweis "${h.titel}" als gelesen`}
                                            />
                                            <div>
                                                <h3 className="h5 mt-1 mb-1" dangerouslySetInnerHTML={{ __html: h.titel }} />
                                                <div className="ms-1" dangerouslySetInnerHTML={{ __html: h.inhalt }} />
                                            </div>
                                        </div>
                                    </section>
                                );
                            })}
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-primary" onClick={requestClose}>
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    return createPortal(
        <div className="bootstrap-iso">{modal}</div>,
        document.body
    );
}

export default HinweiseDisplay;